import { EntityKey } from '../ap-document/ap-document';
import { EntityKeyByReference } from '../bank-account/bank-account';

export enum BankTransactionMovementType {
  INCOME = 0,
  OUTCOME = 1,
  INTERNAL = 2,
}

/** How a movement was executed. Only WIRE, ACH, BANK_INITIATED, UNSPECIFIED, TRANSFER and DEPOSIT are accepted today. */
export enum BankInstrumentType {
  WIRE = 0,
  ACH = 1,
  /** The bank moved the money itself — maintenance charges, interest credits. No reference to match on. */
  BANK_INITIATED = 2,
  /** Not captured. Transitional: flows that do not yet ask for an instrument land here (#5076). */
  UNSPECIFIED = 3,
  CHEQUE = 4,
  CASHIERS_CHEQUE = 5,
  CASH = 6,
  CARD = 7,
  /** Local bank transfer (#6014). The bank reference, when known, goes on the statement reference. */
  TRANSFER = 8,
  /** Deposit at the counter or by slip (#6014). The slip number, when known, goes on the statement reference. */
  DEPOSIT = 9,
}

export enum BankTransactionStatus {
  DRAFT = 0,
  POSTED = 1,
  /** Reserved (#5091) — committed to the books, bank has not paid yet. Nothing sets these today. */
  ISSUED = 2,
  VOID = 3,
  STOPPED = 4,
  RETURNED = 5,
}

/**
 * The kinds of bank charge a movement can carry (#5076). Each resolves its own GL expense account on the
 * API, so the UI never picks an account — only a type and an amount.
 */
export enum BankFeeType {
  BANK = 0,
  SECURITY = 1,
}

/** One bank charge, in the movement's currency. */
export interface BankFee {
  type: BankFeeType;
  amount: number;
}

export interface BankInstrument {
  type: BankInstrumentType;
  endToEndId?: string | null;
  uetr?: string | null;
  chargeBearer?: string | null;
  traceNumber?: string | null;
  effectiveDate?: string | null;
}

export interface BankEndpoint {
  /**
   * Sierra BankAccount code. Null when this endpoint is an external counterparty
   * (INCOME's From, OUTCOME's To). Always set for INTERNAL on both sides.
   */
  code?: string | null;
  amount: number;
  currency: number;
}

/** One line of a movement's GL counter side: which account, and how much of the movement lands on it. */
export interface BankTransactionGlLine {
  /** The GL account, account number included, so a row explains itself without a second lookup. */
  account: EntityKey;
  /** This line's share of the movement, in the movement's currency. The lines sum to the gross. */
  amount: number;
  /** What this line was for. Becomes the journal line's description when set. */
  description?: string | null;
}

export interface BankTransaction {
  id?: string;
  number?: string | null;
  financialTransactionType: number;
  instrument: BankInstrument;
  from?: BankEndpoint | null;
  to?: BankEndpoint | null;
  transactionDate: string;
  postingDate: string;
  fiscalYear: number;
  fiscalPeriod: number;
  financialCompanyReference: EntityKeyByReference;
  concept?: string | null;
  /**
   * The GL accounts posted against, each with the share of the amount it carries. Empty on drafts that
   * have not chosen one.
   *
   * Replaced the single `glCounterAccountReference`. One bank line is often several things — a refund and
   * a rebate arriving as one wire — and one account forced that to be keyed as whichever was larger, or
   * split into two movements the bank never made. Only a movement with no owning document may have more
   * than one line; the API refuses a second on anything a payment or transfer spawned.
   */
  glCounterAccountLines: BankTransactionGlLine[];
  /** Bank charges on this movement, one row per kind (#5076). Always a cost we bear. */
  fees: BankFee[];

  /**
   * Transaction currency to functional (USD) multiplier, stamped from the journal entry at post time.
   * Read-only — never entered here.
   */
  exchangeRate?: number | null;
  bankingReference?: string | null;
  description?: string | null;
  notes?: string | null;
  status: number;
  /** The entry that booked this movement, with its document number. Null until the movement posts. */
  journalEntryReference?: EntityKey | null;

  /**
   * The document this movement belongs to (#5140) — an internal transfer, an AP payment, an advance, a
   * cash funding, a withholding remittance. Null on a movement someone entered by hand.
   *
   * <p>Its presence means the movement's accounts and amount are owned by that document, so it must not be
   * edited on the standalone movement screen. Read `entityDefinitionCode` before acting on it: the owner is
   * not necessarily a transfer, and only a transfer has a screen that can open one of its legs.</p>
   */
  ownerKey?: EntityKey | null;
  reconciledOn?: string | null;
  /** Renamed from mt940Reference (#5086) — the statement source is not necessarily MT940. */
  matchedStatementLineRef?: string | null;
  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

/** GlobalEntityDefinition.InternalTransfer (62) — the one owner with a screen that can open its legs. */
export const INTERNAL_TRANSFER_ENTITY_CODE = 62;

/** GlobalEntityDefinition.FinancialAccount (56) — the entity code for a GL counter account (#5076). */
export const FINANCIAL_ACCOUNT_ENTITY_CODE = 56;

/** GlobalEntityDefinition.FinancialCompany. Not the site id — a site maps to a company, it isn't one. */
export const FINANCIAL_COMPANY_ENTITY_CODE = 57;
